import { FormEvent } from 'react';

import { IconAlertCircle, IconCamera, IconCheck, IconLogout, IconUser } from '@tabler/icons-react';

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Transition,
} from '@optiro/ui';

import { AccountAvatar } from '@/components/AccountAvatar';
import { CalendarSettings } from '@/components/CalendarSettings';
import { Account, userData } from '@/types/types';

export interface AccountSettingsViewProps {
  userData: userData;
  opened: boolean;
  onOpen: () => void;
  onClose: () => void;
  formState: Partial<Account>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileChange: (file: File | null) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleLogout: () => void;
  isSaving: boolean;
  isPhotoLoading: boolean;
  isLoggingOut: boolean;
  showSuccess: boolean;
  hasError: boolean;
}

export const AccountSettingsView = ({
  userData,
  opened,
  onOpen,
  onClose,
  formState,
  handleChange,
  handleFileChange,
  handleSubmit,
  handleLogout,
  isSaving,
  isPhotoLoading,
  isLoggingOut,
  showSuccess,
  hasError,
}: AccountSettingsViewProps) => {
  return (
    <>
      <Button
        loading={isSaving || isPhotoLoading}
        variant="outline"
        style={{ borderColor: 'var(--mantine-color-gray-3)' }}
        py={2}
        px={'sm'}
        radius="xl"
        bg="gray.0"
        onClick={onOpen}
      >
        <Group justify="flex-start" gap="4">
          <AccountAvatar
            photoUrl={userData?.photo.url}
            name={userData?.name ?? ''}
            radius="xl"
            size="sm"
          />
          <Stack gap={0} align="flex-start" justify="center">
            <Text fw={600} size="sm" c="brand.9" style={{ lineHeight: 1 }}>
              {userData?.name}
            </Text>
            {userData?.company_account_category && (
              <Text fw={500} size="xs" c="dimmed" style={{ lineHeight: 1 }}>
                {userData?.company_account_category.name}
              </Text>
            )}
          </Stack>
        </Group>
      </Button>

      <Modal
        opened={opened}
        onClose={onClose}
        size="lg"
        centered
        radius="md"
        title={
          <Group gap="xs">
            <Title order={4}>Kontoindstillinger</Title>
          </Group>
        }
      >
        <Stack gap="lg">
          {/* Success message */}
          <Transition
            mounted={showSuccess}
            transition="slide-down"
            duration={300}
            timingFunction="ease"
          >
            {(styles) => (
              <Paper style={styles} shadow="sm" p="md" radius="md" withBorder bg="green.0">
                <Group gap="sm">
                  <IconCheck size={20} color="green" />
                  <Text c="green.7" fw={500}>
                    Dine ændringer er gemt!
                  </Text>
                </Group>
              </Paper>
            )}
          </Transition>

          {/* Error message */}
          {hasError && (
            <Paper shadow="sm" p="md" radius="md" withBorder bg="red.0">
              <Group gap="sm">
                <IconAlertCircle size={20} color="red" />
                <Text c="red.7" fw={500}>
                  Der opstod en fejl ved opdatering
                </Text>
              </Group>
            </Paper>
          )}

          {/* Profile Section */}
          <Paper p="lg" radius="md" withBorder bg="gray.0">
            <Group gap="xl" align="center">
              <Box style={{ position: 'relative' }}>
                {isPhotoLoading ? (
                  <Center
                    w={100}
                    h={100}
                    bg="white"
                    style={{
                      borderRadius: '50%',
                      border: '4px solid var(--mantine-color-gray-3)',
                    }}
                  >
                    <Loader />
                  </Center>
                ) : (
                  <>
                    <AccountAvatar
                      photoUrl={userData?.photo.url}
                      name={userData?.name ?? ''}
                      alt="Profilbillede"
                      size={100}
                      radius={100}
                      style={{
                        border: '4px solid #fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s ease',
                      }}
                      className="avatar-hover"
                    />
                    <ActionIcon
                      variant="filled"
                      color="blue"
                      size="lg"
                      radius="xl"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                      onClick={() => document.getElementById('photo-input')?.click()}
                    >
                      <IconCamera size={18} />
                    </ActionIcon>
                  </>
                )}
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                />
              </Box>

              <Stack gap="xs">
                <Text fw={600} size="lg">
                  {userData?.name}
                </Text>
                <Text size="md" c="dimmed">
                  {userData?.email}
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  Klik på kameraet for at ændre profilbillede
                </Text>
              </Stack>
            </Group>
          </Paper>

          <Divider label="Personlige oplysninger" labelPosition="left" />

          {/* Info Form */}
          <Paper p="lg" radius="md" withBorder>
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Navn"
                  name="name"
                  placeholder="Indtast dit navn"
                  value={formState.name}
                  onChange={handleChange}
                  required
                  size="md"
                  leftSection={<IconUser size={16} />}
                />

                <Button
                  type="submit"
                  loading={isSaving}
                  fullWidth
                  size="md"
                  leftSection={!isSaving && <IconCheck size={16} />}
                >
                  {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                </Button>
              </Stack>
            </form>
          </Paper>

          <Divider label="Kalender" labelPosition="left" />

          {/* Calendar Settings */}
          <CalendarSettings accountId={userData.id} />

          <Divider />

          {/* Logout Section */}
          <Paper p="md" radius="md" withBorder bg="red.0">
            <Stack gap="sm">
              <Group gap="xs">
                <IconLogout size={20} color="var(--mantine-color-red-6)" />
                <Text fw={500} c="red.7">
                  Log ud af din konto
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Du vil blive logget ud af alle enheder
              </Text>
              <Button
                leftSection={<IconLogout size={18} />}
                color="red"
                variant="outline"
                onClick={handleLogout}
                loading={isLoggingOut}
                fullWidth
                size="md"
              >
                {isLoggingOut ? 'Logger ud...' : 'Log ud'}
              </Button>
            </Stack>
          </Paper>
        </Stack>

        <style>{`
          .avatar-hover:hover {
            transform: scale(1.05);
          }
        `}</style>
      </Modal>
    </>
  );
};
